import { useState, useEffect, type Dispatch, type SetStateAction } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import {
  Row,
  Button,
  Radio,
  LoadingSkeleton,
  TextInput,
  Chip,
  ContentCard,
  Alert,
} from "@narmi/design_system";
import {
  useFetchPaginatedBulkTemplates,
  useFormattedPaginatedBulkTemplates,
} from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import {
  useBulkTemplatePermissions,
  getTemplateFilter,
} from "byzantine/src/dbbl/businessLogic/modules/bulkTemplates";
import type { FormattedBulkTemplate } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates/types";
import HeaderWithSteps from "../../../HeaderWithSteps";
import Container from "../../../Container";
import { TEMPLATES_ROUTE } from "../../../Templates/constants";
import { PAYMENT_ACTIVITY_ROUTE } from "../../routes";
import { useTemplateContext } from "./TemplateContext";
import {
  getTemplateNetTotal,
  getTemplateAccountDisplay,
} from "./templateHelpers";
import styles from "../CreateBulk.module.scss";

type StartOption = "template" | "manual";

const TemplateRow = ({
  template,
  selected,
  onClick,
}: {
  template: FormattedBulkTemplate;
  selected: boolean;
  onClick: () => void;
}) => {
  const { l10n } = useLocalization();
  const accountDisplay = getTemplateAccountDisplay(template) ?? "";

  const separator = (
    <span className={`narmi-icon-solid-circle ${styles.separator}`} />
  );

  const netTotal = getTemplateNetTotal(template, { hasDecimal: false });

  const detailParts = [
    l10n.getString("bulk-template-payment-count", {
      count: template.paymentCount,
    }),
    netTotal,
    accountDisplay,
  ].filter(Boolean);

  return (
    <div className={styles.templateRow}>
      <ContentCard
        kind="toggle"
        isSelected={selected}
        onClick={onClick}
        paddingSize="m"
      >
        <div className={styles.templateRowHeader}>
          <span className="fontWeight--semibold">{template.name}</span>
          {template.state === "active_pending" && (
            <Chip
              kind="secondary"
              label={l10n.getString("bulk-template-pending-updates-chip")}
            />
          )}
        </div>
        <p className="fontSize--s fontColor--secondary margin--top--xxs">
          {detailParts.map((part, i) => (
            <span key={i}>
              {i > 0 && separator}
              {part}
            </span>
          ))}
        </p>
      </ContentCard>
    </div>
  );
};

const TemplateListContent = ({
  templates,
  debouncedSearchValue,
  canEdit,
  searchValue,
  setSearchValue,
  selectedTemplateId,
  onSelectTemplate,
}: {
  templates: FormattedBulkTemplate[];
  debouncedSearchValue: string;
  canEdit: boolean;
  searchValue: string;
  setSearchValue: Dispatch<SetStateAction<string>>;
  selectedTemplateId: string | null;
  onSelectTemplate: (id: string) => void;
}) => {
  const { l10n } = useLocalization();
  const isEmpty = templates.length === 0 && !debouncedSearchValue;

  if (isEmpty && !canEdit) {
    return (
      <>
        <p className="fontWeight--semibold fontColor--primary">
          {l10n.getString("message-no-ach-templates-no-permission-1")}
        </p>
        <p className="fontWeight--semibold fontColor--primary margin--top--xs">
          {l10n.getString("message-no-ach-templates-no-permission-2")}
        </p>
      </>
    );
  }

  if (isEmpty && canEdit) {
    return (
      <p className="fontColor--secondary">
        {l10n.getString("message-no-ach-templates")}
      </p>
    );
  }

  return (
    <>
      <div className="margin--bottom--m">
        <TextInput
          startIcon="search"
          type="search"
          placeholder={l10n.getString("search-templates-label")}
          value={searchValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchValue(e.target.value)
          }
        />
      </div>
      <div className={styles.templateList}>
        {templates.map((template) => (
          <TemplateRow
            key={template.uuid}
            template={template}
            selected={selectedTemplateId === template.uuid}
            onClick={() => onSelectTemplate(template.uuid)}
          />
        ))}
        {templates.length === 0 && debouncedSearchValue && (
          <div
            className={`padding--all--m margin--top--m margin--bottom--m textAlign--center ${styles.emptySearch}`}
          >
            <h4 className="fontSize--l">
              {l10n.getString("bulk-template-search-empty")}
            </h4>
            <p className="fontColor--secondary margin--top--xxs">
              {l10n.getString("bulk-template-search-empty-subtitle")}
            </p>
          </div>
        )}
      </div>
    </>
  );
};

const SelectTemplateScreen = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const templateIdFromUrl = searchParams.get("templateId");
  const { hasPopulatedForm } = useTemplateContext();
  const permissions = useBulkTemplatePermissions();
  const { canEdit, canMakePayment } = permissions;
  const templateFilter = getTemplateFilter(permissions);

  // default to "template" on first visit
  const getInitialStartOption = (): StartOption => {
    if (templateIdFromUrl) return "template";
    if (hasPopulatedForm) return "manual";
    return "template";
  };

  const [startOption, setStartOption] = useState<StartOption>(
    getInitialStartOption()
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    templateIdFromUrl
  );
  const [showTemplateError, setShowTemplateError] = useState(false);

  const handleSelectTemplate = (id: string | null) => {
    setSelectedTemplateId(id);
    setShowTemplateError(false);
    if (id) {
      setSearchParams({ templateId: id }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchValue(searchValue), 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  const { send: fetchPage, loading } = useFetchPaginatedBulkTemplates();
  const templates = useFormattedPaginatedBulkTemplates(0);

  useEffect(() => {
    if (!canMakePayment) return;
    const params = ["state_in=active,active_pending"];
    if (templateFilter) {
      params.push(`payment_type_in=${templateFilter}`);
    }
    if (debouncedSearchValue) {
      params.push(`search=${encodeURIComponent(debouncedSearchValue)}`);
    }
    fetchPage(0, 0, 100, params.join("&"));
  }, [fetchPage, debouncedSearchValue, templateFilter, canMakePayment]);

  // if the user can't send or collect via template, skip to manual entry
  if (!canMakePayment) {
    return (
      <Navigate to={`/${PAYMENT_ACTIVITY_ROUTE}/bulk/new/details`} replace />
    );
  }

  const handleClickNext = () => {
    if (startOption === "template" && !selectedTemplateId) {
      setShowTemplateError(true);
      return;
    }
    if (startOption === "template") {
      navigate(
        `/${PAYMENT_ACTIVITY_ROUTE}/bulk/new/details?templateId=${selectedTemplateId}`
      );
    } else if (startOption === "manual") {
      navigate(`/${PAYMENT_ACTIVITY_ROUTE}/bulk/new/details`);
    }
  };

  return (
    <Container maxWidth="640px">
      <HeaderWithSteps headerText={l10n.getString("bulk-payment-header")} />
      <div className="margin--top--xl">
        <h4 className="fontSize--m">
          {l10n.getString("bulk-payment-start-option-title")}
        </h4>
        <div className="margin--top--m">
          <Radio
            kind="input-card"
            name="startOption"
            value="template"
            checked={startOption === "template"}
            onCheck={() => {
              setStartOption("template");
              setShowTemplateError(false);
            }}
          >
            <h4 className="fontFamily--body fontSize--m fontWeight--semibold">
              {l10n.getString("bulk-payment-start-option-template-title")}
            </h4>
            <p className="fontSize--s margin--top--xxs fontColor--secondary">
              {l10n.getString("bulk-payment-start-option-template-description")}
            </p>
          </Radio>
          <div className="margin--top--s">
            <Radio
              kind="input-card"
              name="startOption"
              value="manual"
              checked={startOption === "manual"}
              onCheck={() => {
                setStartOption("manual");
                setSelectedTemplateId(null);
                setSearchParams({}, { replace: true });
                setShowTemplateError(false);
              }}
            >
              <h4 className="fontFamily--body fontSize--m fontWeight--semibold">
                {l10n.getString("bulk-payment-start-option-manual-title")}
              </h4>
              <p className="fontSize--s margin--top--xxs fontColor--secondary">
                {l10n.getString("bulk-payment-start-option-manual-description")}
              </p>
            </Radio>
          </div>
        </div>

        {startOption === "template" && (
          <>
            <hr className="margin--top--xl margin--bottom--xl" />
            <ContentCard kind="bordered" paddingSize="l">
              <div className={styles.templateHeader}>
                <h4 className="fontSize--l">
                  {l10n.getString("ach-templates-tab")}
                </h4>
                {canEdit && (
                  <Button
                    kind="plain"
                    label={l10n.getString("new-template")}
                    onClick={() => navigate(`/${TEMPLATES_ROUTE}/ach/new`)}
                  />
                )}
              </div>
              {showTemplateError && (
                <div className="margin--bottom--m">
                  <Alert isActive kind="error" isDismissable={false}>
                    {l10n.getString("bulk-payment-select-template-error")}
                  </Alert>
                </div>
              )}
              <LoadingSkeleton isLoading={loading}>
                <TemplateListContent
                  templates={templates}
                  debouncedSearchValue={debouncedSearchValue}
                  canEdit={canEdit}
                  searchValue={searchValue}
                  setSearchValue={setSearchValue}
                  selectedTemplateId={selectedTemplateId}
                  onSelectTemplate={handleSelectTemplate}
                />
              </LoadingSkeleton>
            </ContentCard>
          </>
        )}
      </div>

      <div className="margin--bottom--xl margin--top--xl">
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="negative"
              label={l10n.getString("button-cancel")}
              onClick={() => {
                navigate(`/${PAYMENT_ACTIVITY_ROUTE}/bulk`);
              }}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("button-next")}
              onClick={handleClickNext}
            />
          </Row.Item>
        </Row>
      </div>
    </Container>
  );
};

export default SelectTemplateScreen;
